/**
 * Profile + role context.
 *
 * Wraps the signed-in Firebase user with their /users/{email} document AND
 * the live /config/app document. Together these tell us:
 *   - Is the user's profile complete? (payoutCurrency != null)
 *   - Is the user an admin? (email in config.adminEmails)
 *   - Is the user accounting? (email == config.accountingEmail)
 *
 * Subscribed live to both documents — changes propagate to the UI without
 * a refresh. This is critical for the case where an admin adds someone's
 * profile while they're sitting on the "contact HR" screen: the screen
 * should update on its own.
 *
 * The `onUserSignedUp` trigger usually creates the /users doc within a few
 * hundred ms of first sign-in. While we wait we surface a 'provisioning'
 * status so the UI shows a spinner rather than a misleading "contact HR".
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import type { User as FirebaseUser } from 'firebase/auth';
import { db } from '../lib/firebase';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig, User as UserDoc } from '@daaily/shared';
import { subscribeRestrictedConfig } from '../lib/restrictedConfig';

type ProfileState =
  | { status: 'loading' }                            // auth/firestore still loading
  | { status: 'provisioning' }                       // user doc not yet created by trigger
  | { status: 'incomplete'; user: UserDoc; config: AppConfig }   // exists but no payoutCurrency
  | { status: 'inactive'; user: UserDoc; config: AppConfig }     // active === false
  | { status: 'ready'; user: UserDoc; config: AppConfig; isAdmin: boolean; isAccounting: boolean };

interface ProfileContextValue {
  state: ProfileState;
  /** The Firebase auth user — null when signed out. Convenience pass-through. */
  authUser: FirebaseUser | null;
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);


export function ProfileProvider({ children }: { children: ReactNode }) {
  const { state: authState } = useAuth();
  const [userDoc, setUserDoc] = useState<UserDoc | null | 'unknown'>('unknown');
  const [config, setConfig] = useState<AppConfig | null | 'unknown'>('unknown');
  // The old client-side "View as user" preview was removed (Oct 2026): it
  // changed menus but not server data. Clear its stale per-tab flag once.
  useEffect(() => {
    try { sessionStorage.removeItem('daaily.viewAsUser'); } catch { /* ignore */ }
  }, []);

  // Subscribe to /users/{email}
  useEffect(() => {
    if (authState.status !== 'signed_in') {
      setUserDoc('unknown');
      return;
    }
    const email = authState.user.email?.toLowerCase();
    if (!email) {
      setUserDoc('unknown');
      return;
    }
    const userRef = doc(db, 'users', email);
    const unsub = onSnapshot(
      userRef,
      (snap) => setUserDoc(snap.exists() ? (snap.data() as UserDoc) : null),
      (err) => {
        console.error('[ProfileProvider] users snapshot error:', err);
        // Keep userDoc as 'unknown' on error so the UI shows a loading state
        // rather than misleading content.
      },
    );
    return unsub;
  }, [authState.status, authState.status === 'signed_in' ? authState.user.uid : null]);

  // Subscribe to /config/app
  useEffect(() => {
    if (authState.status !== 'signed_in') {
      setConfig('unknown');
      return;
    }
    const configRef = doc(db, 'config', 'app');
    // /config/app + /config/restricted (compensation figures; only readable by
    // admins and commission/USL managers) merged, like the server's getAppConfig.
    let app: AppConfig | null | undefined;
    let restricted: Record<string, unknown> | undefined;
    const emit = () => {
      if (app === undefined || restricted === undefined) return;
      setConfig(app === null ? null : ({ ...app, ...restricted } as AppConfig));
    };
    const unsub = onSnapshot(
      configRef,
      (snap) => { app = snap.exists() ? (snap.data() as AppConfig) : null; emit(); },
      (err) => {
        console.error('[ProfileProvider] config snapshot error:', err);
      },
    );
    const unsubRestricted = subscribeRestrictedConfig((d) => { restricted = d; emit(); });
    return () => { unsub(); unsubRestricted(); };
  }, [authState.status]);

  let state: ProfileState;
  const authUser = authState.status === 'signed_in' ? authState.user : null;

  if (authState.status !== 'signed_in') {
    state = { status: 'loading' };
  } else if (userDoc === 'unknown' || config === 'unknown') {
    state = { status: 'loading' };
  } else if (userDoc === null) {
    // /users doc does not exist yet — the onUserSignedUp trigger should
    // create it momentarily. If it never appears, the user is in trouble
    // (probably a non-daaily account that slipped past auth — but the rules
    // would also have rejected the read).
    state = { status: 'provisioning' };
  } else if (config === null) {
    // /config/app missing — system is not configured. This is an admin
    // problem (run seed:config), not a user problem.
    state = { status: 'loading' };
  } else if (!userDoc.active) {
    state = { status: 'inactive', user: userDoc, config };
  } else if (userDoc.payoutCurrency == null) {
    state = { status: 'incomplete', user: userDoc, config };
  } else {
    // Expense-tool admin = in the legacy adminEmails list, OR a hub
    // super_admin (the legacy hubRole 'admin' is treated the same during the
    // transition). Mirrors the server, where super_admins bypass all auth.
    const isSuperAdmin =
      userDoc.hubRole === 'super_admin' || userDoc.hubRole === 'admin';
    state = {
      status: 'ready',
      user: userDoc,
      config,
      isAdmin: config.adminEmails.includes(userDoc.email) || isSuperAdmin,
      isAccounting: config.accountingEmails.includes(userDoc.email),
    };
  }

  return (
    <ProfileContext.Provider value={{ state, authUser }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used inside <ProfileProvider>');
  return ctx;
}
